import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { useCallback, useMemo } from "react";
import type { InjectedFormProps } from "redux-form";
import { reduxForm } from "redux-form";
import { CalloutIntent, DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { ATTRIBUTE_ID, ATTRIBUTE_TYPE } from "@/settings/settings.js";

import { trimOrThrow } from "../../app-utils/string.js";
import { freeTextValidation, numberValidation, whiteSpaceValidation } from "../../app-utils/validation.js";
import {
  type Attribute,
  type InputAttributeDefaultInput,
  type RawAttributeDefault,
  type VocabularyTerm,
} from "../../graphql.js";
import { getAttributeLabel, isDefinedNumber } from "../../redux/modules/sample-admin.js";
import { SCRIPTING_MACRO } from "../core/forms/ScriptInputField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { ScriptInputReduxFormField } from "../core/redux-form/ScriptInputField.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";

interface IConfigureSampleTypeAttributeDialogOwnProps {
  onApply?: (attributeId: number, configurationPayload: InputAttributeDefaultInput) => void;
  onCancel?: () => void;

  templateAttributes: Attribute[];
  attribute: IAttributeWithDefault;
}

export interface IConfigureSampleTypeAttributeDialogProps
  extends IConfigureSampleTypeAttributeDialogOwnProps,
    InjectedFormProps<FormData, IConfigureSampleTypeAttributeDialogOwnProps> {}

export const CONFIGURE_TYPE_ATTRIBUTE_FORM_NAME = "CONFIGURE_SAMPLE_TYPE_ATTRIBUTE_FORM";
export const CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER = "defaultNumber";
export const CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT = "defaultText";
export const CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM = "defaultVocTerm";
export const CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE = "template";

const ConfigureSampleTypeAttributeDialogImpl = (props: IConfigureSampleTypeAttributeDialogProps): JSX.Element => {
  const { overlayProps, state } = useOverlay({
    defaultOpen: true,
    onOpenChange(isOpen) {
      if (!isOpen) {
        props.onCancel?.();
      }
    },
  });

  const validateNumber = useCallback(
    (value: string): string | undefined => {
      if (!value || value.trim().length === 0) {
        return undefined;
      }
      return numberValidation(value, props.attribute);
    },
    [props.attribute],
  );

  const validateFreeText = useCallback(
    (value: string): string | undefined => {
      if (!value || value.trim().length === 0) {
        return undefined;
      }
      return whiteSpaceValidation(value) || freeTextValidation(value, props.attribute);
    },
    [props.attribute],
  );

  const { attribute, onApply } = props;

  const handleSubmitData = useCallback(
    (values: FormData): Promise<boolean> => {
      const numberDefault = values[CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER];
      const configurationPayload: Partial<RawAttributeDefault> = {
        defaultNumber: !numberDefault || numberDefault.length === 0 ? null : Number(numberDefault),
        defaultText: trimOrThrow(values[CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT]),
        defaultTerm: values[CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM],
        template: trimOrThrow(values[CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE]),
      };

      onApply?.(attribute.id, configurationPayload);
      return Promise.resolve(true);
    },
    [attribute.id, onApply],
  );

  const footerMessage = useMemo(() => {
    if (props.error) {
      return { message: props.error, intent: CalloutIntent.Danger };
    }
    return undefined;
  }, [props.error]);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title="Configure Attribute"
        mainButton={{
          label: "Apply",
          isDisabled: props.submitting || props.invalid,
          onPress: props.handleSubmit(handleSubmitData),
        }}
        errorCallout={
          footerMessage && {
            message: footerMessage.message,
            intent: footerMessage.intent,
          }
        }
        closeFn={state.close}
      >
        <div>
          <p>
            You can add some specific configuration for attribute {getAttributeLabel(props.attribute)} when used in the
            context of this sample type.
          </p>
          {props.attribute.id === ATTRIBUTE_ID.SAMPLE_NAME ? (
            <p>If you decide not to specify a template, the sample names will be defaulted to a standard.</p>
          ) : null}
          {props.attribute.id !== ATTRIBUTE_ID.SAMPLE_NAME &&
            props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
              <InputReduxFormField
                componentClass="input"
                name={CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT}
                label={"Default text"}
                placeholder={`Enter default text`}
                // maxLength can not be 0
                maxLength={props.attribute.maxLength || DB_FIELD_MAX_LENGTH.ATTRIBUTE.DEFAULT_TEXT}
                component={InputField}
                validate={validateFreeText}
              />
            )}

          {props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
            <ScriptInputReduxFormField
              name={CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE}
              title={"Template"}
              maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.TEMPLATE}
              attributes={props.templateAttributes}
              acceptedMacros={[SCRIPTING_MACRO.DATE, SCRIPTING_MACRO.PARENT_TYPE]}
            />
          )}

          {props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL && (
            <InputReduxFormField
              componentClass="input"
              type="number"
              name={CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER}
              label={"Default number"}
              placeholder={`Enter default number`}
              component={InputField}
              validate={validateNumber}
            />
          )}

          {props.attribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT && (
            <ReactSelectReduxFormField
              name={CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM}
              label={"Default term"}
              placeholder="Select a default..."
              component={ReactSelectField}
              options={props.attribute.vocabularyTerms}
              isSearchable
              closeMenuOnSelect
              openMenuOnFocus
              isClearable={true}
              isMulti={false}
              getOptionValue={(o: VocabularyTerm | null) => o && o.id}
              getOptionLabel={(o: VocabularyTerm | null) => o && o.name}
            />
          )}
        </div>
      </DialogComponent>
    </Modal>
  );
};

const validate = (values: FormData): { _error?: string } => {
  const defaultText: string = values[CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT];
  const template: string = values[CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE];

  if (!!defaultText && !!template) {
    return { _error: "You can fill only one of default text or template input fields." };
  }
  return {};
};
const ConfigureSampleTypeAttributeDialogForm = reduxForm<FormData, IConfigureSampleTypeAttributeDialogOwnProps>({
  form: CONFIGURE_TYPE_ATTRIBUTE_FORM_NAME,
  validate, // we use field level validation
})(ConfigureSampleTypeAttributeDialogImpl);

const ConfigureSampleTypeAttributeDialog = (props: IConfigureSampleTypeAttributeDialogOwnProps): JSX.Element => {
  const initialValues = useMemo(() => {
    const defaults = props.attribute.attributeDefault;
    return {
      [CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER]:
        defaults && isDefinedNumber(defaults.defaultNumber) ? String(defaults.defaultNumber) : null,
      [CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT]: defaults?.defaultText,
      [CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM]: defaults?.defaultTerm,
      [CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE]: defaults?.template,
    };
  }, [props.attribute]);

  return <ConfigureSampleTypeAttributeDialogForm {...props} initialValues={initialValues} />;
};

export { ConfigureSampleTypeAttributeDialog, ConfigureSampleTypeAttributeDialogImpl };
export default ConfigureSampleTypeAttributeDialog;
